import { WarningCircleIcon } from "@phosphor-icons/react";
import { isRouteErrorResponse, useRouteError } from "react-router";

import { NotFoundScreen } from "./NotFoundScreen";

/**
 * The shared convention for a screen that could not be rendered.
 *
 * It never prints the error. A router error can carry a server message, a
 * stack, a correlation value or a fragment of a request, and none of that
 * belongs on a shop owner's screen. Diagnostics stay in the console and, once
 * the API exists, in the correlated server log.
 *
 * It also offers no action that could repeat a financial operation. Reloading a
 * view is safe; retrying an unknown write is not, which is what
 * `ConfirmDialog`'s unknown-outcome state is for.
 */
export function RouteErrorScreen() {
  const error = useRouteError();

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundScreen />;
  }

  return (
    <section aria-labelledby="route-error-title" className="max-w-2xl" role="alert">
      <WarningCircleIcon aria-hidden="true" className="size-8 text-danger" weight="duotone" />
      <h1 className="mt-5 text-3xl font-semibold tracking-tight" id="route-error-title">
        This screen could not be opened
      </h1>
      <p className="mt-3 leading-7 text-text-muted">
        Nothing was changed. Reload the workspace and open the screen again. If it keeps failing, tell
        support which screen you were opening and when.
      </p>
    </section>
  );
}
